<template>
  <div class="w-750 h-full pr-20 md:pr-0">
    <div
      v-if="!hideHeader"
      class="border-t border-b flex items-center justify-between pl-10 font-semibold py-9 text-15"
      :class="isDarkMode ? 'border-hover' : 'border-grey-500'"
    >
      <div v-for="item in menuItems" :key="item.name" :class="[item.class, {'hidden': hiddenItems.includes(item.name)}]">
        <div
          class="py-5 px-10 rounded-8 flex items-center anim-color"
          :class="{'hover:bg-grey-150 cursor-pointer': sortByItem.includes(item.name)}"
          @click="sort(item.name)"
        >
          {{ item.name }}
          <transition name="fade">
            <i
              v-if="item.name === sortedBy && sortByItem.includes(item.name)"
              class="icon-arr-left text-20 ml-5 text-brand font-bold transform"
              :class="sortDirection ? '-rotate-90' : 'rotate-90'"
            />
          </transition>
        </div>
      </div>
      <div class="w-70 text-center" :class="{'opacity-0': hiddenItems.includes('Billing')}">Billing</div>
      <div class="w-100 text-center">Delete</div>
    </div>

    <div class="pl-20">
      <div v-if="members && members.length === 0" class="my-20 text-grey-300">
        {{ noMembersMessage }}
      </div>

      <div v-else class="mt-20">
        <component
          :is="component"
          v-for="member in members"
          :key="member.id"
          :member="member"
          class="mb-20"
        />
      </div>
    </div>

    <div
      v-if="!hideFooter"
      class="border-t py-7 flex items-center justify-between"
      :class="isDarkMode ? 'border-hover' : 'border-grey-500'"
    >
      <div
        class="flex items-center opacity-50 anim-opacity pl-10"
        :class="[
          isDarkMode ? 'bg-transparent border-b-2 border-dark-900 rounded-none' : 'bg-grey-850 pl-10 rounded-6',
          {'opacity-100': isFocused},
        ]"
      >
        <i class="icon-search text-app-color cursor-pointer" @click="toggleInput" />
        <InputModule
          ref="inputRef"
          v-model="searchValue"
          name="searchValue"
          type="text"
          placeholder="search team member"
          class="w-185 ml-10"
          shadow=""
          :has-background="false"
          input-classes="text-app-color focus:outline-none py-11 leading-normal bg-transparent
                   placeholder-app-color anim-opacity text-13"
          @focus="isFocused = true"
          @blur="isFocused = false"
          @input="search"
        />
      </div>
      <PaginationModule
        :total-items="totalMembers"
        :current-page="page"
        :items-per-page="limit"
        @updatePage="updatePage"
      />
    </div>
  </div>
</template>

<script>
import { computed, defineComponent, ref } from 'vue'
import Member from '@/components/team/Member'
import TemporaryMember from '@/components/team/TemporaryMember'
import PaginationModule from '@/components/shared/PaginationModule'
import InputModule from '@/components/shared/form-controls/InputModule'
import { useStore } from 'vuex'

export default defineComponent({
  components: { Member, TemporaryMember, PaginationModule, InputModule },

  props: {
    members: Array,
    totalMembers: Number,

    sortByItem: {
      type: Array,
      default: () => ['Name', 'Spaces', 'Role']
    },

    hiddenItems: {
      type: Array,
      default: () => []
    },

    component: {
      type: String,
      default: 'Member'
    },

    hideHeader: {
      type: Boolean,
      default: false
    },

    hideFooter: {
      type: Boolean,
      default: false
    },

    noMembersMessage: String
  },

  emits: ['getMembers'],

  setup (props, { emit }) {
    const { getters } = useStore()

    let timer = null

    const sortedBy = ref('Name')
    const sortDirection = ref(true)
    const page = ref(1)
    const limit = ref(5)

    const searchValue = ref('')
    const isFocused = ref(false)
    const inputRef = ref(null)

    const isDarkMode = computed(() => getters.isDarkMode)

    const menuItems = [{
      name: 'Name',
      class: 'flex-grow pr-10 flex anim-opacity'
    }, {
      name: 'Spaces',
      class: 'w-150 flex'
    }, {
      name: 'Role',
      class: 'w-full max-w-170 flex'
    }]

    function sort (value) {
      if (!props.sortByItem.includes(value)) return

      if (sortedBy.value === value) {
        sortDirection.value = !sortDirection.value
      } else {
        sortedBy.value = value
        sortDirection.value = true
      }

      getMember()
    }

    function getMember () {
      emit('getMembers', {
        limit: limit.value,
        page: page.value - 1,
        column: sortedBy.value.toLowerCase(),
        direction: sortDirection.value ? 'ASC' : 'DESC',
        search: searchValue.value
      })
    }

    function updatePage (value) {
      page.value = value
      getMember()
    }

    function toggleInput () {
      inputRef.value.focus()
    }

    function search () {
      clearTimeout(timer)

      timer = setTimeout(() => getMember(), 300)
    }

    return {
      menuItems,
      sortedBy,
      sortDirection,
      page,
      limit,
      searchValue,
      isFocused,
      isDarkMode,
      inputRef,
      sort,
      updatePage,
      toggleInput,
      search
    }
  }
})
</script>
